import { useMemo } from 'react';

// -----------------------------------------------------------------------------
// The photo wall behind sign-in.
//
// A port of the mobile app's `Shared/features/Auth/components/AuthBackdrop.tsx`,
// so a staff member who has seen the product recognises this screen. Same grid,
// same -13° slant, same violet washes, same drift - rebuilt on CSS keyframes
// instead of Reanimated.
//
// HOW THE LOOP STAYS SEAMLESS. Each row renders its tile list TWICE, giving it
// two cycles of content, and animates `translateX` by exactly one cycle before
// snapping back to zero. The second copy is always covering whatever the first
// copy has vacated, so there is never a visible seam or a reset. Rows alternate
// direction and carry a small per-row variance in duration, which is what makes
// the wall read as parallax rather than as one sheet sliding.
//
// The plate is sized from the ROTATED bounding box of the area it has to cover -
// `w·cos θ + h·sin θ` and its transpose - plus a tile of slack on each axis.
// Sizing it to the viewport instead leaves triangular gaps at two corners, which
// is the failure this arithmetic exists to prevent.
//
// RESPONSIVE. On a phone the wall occupies the top half and dissolves into white
// exactly as it does in the app. From `lg` it covers the whole viewport and the
// dissolve becomes a horizontal one, so the form's card floats on white at the
// centre with the wall alive around it. One component, two masks - the animation
// itself never changes.
//
// ⚠ THE TILES ARE REMOTE IMAGES. `index.css` states the intent that this panel
// be runnable behind a strict CSP with no external origins; nothing enforces one
// today, but a deployment that adds one needs
// `img-src 'self' data: https://images.unsplash.com`. If that is unacceptable,
// download the twelve files into `src/assets/` and import them - the layout does
// not care where the URLs come from. Every tile also sits on a neutral
// background, so a blocked image degrades to a grey card rather than a hole.
// -----------------------------------------------------------------------------

/** Figma canvas the tile metrics were measured on, matching the app. */
const REF_WIDTH = 393;
const TILE_WIDTH = 134.3;
const TILE_HEIGHT = 138.8;
const TILE_GAP = 17.9;
const TILE_RADIUS = 13.43;

const SLANT_DEGREES = -13;

/** Seconds a row spends travelling one tile, before the per-row variance. */
const SECONDS_PER_TILE = 9;

/** The same twelve stills the app uses. */
const PHOTOS = [
  'https://images.unsplash.com/photo-1594381898411-846e7d193883?w=400',
  'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400',
  'https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?w=400',
  'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400',
  'https://images.unsplash.com/photo-1548690312-e3b507d8c110?w=400',
  'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400',
  'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400',
  'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=400',
  'https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=400',
  'https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?w=400',
  'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=400',
  'https://images.unsplash.com/photo-1550345332-09e3ac987658?w=400',
];

/** The violet / indigo washes painted over roughly every third tile. */
const TINTS: (string | null)[] = [
  'rgba(64,0,184,0.34)',
  null,
  'rgba(29,0,83,0.30)',
  null,
  null,
  'rgba(138,105,198,0.28)',
  null,
  'rgba(50,52,175,0.26)',
  null,
];

/**
 * Laid out for a fixed 1440×900 reference rather than measured live.
 *
 * A `useWindowDimensions` equivalent would re-run this on every resize frame and
 * rebuild the whole grid mid-animation, restarting every row. The plate is
 * generously oversized and centred, so one fixed layout covers every viewport
 * from a 360px phone to a 32" monitor without ever needing to change.
 */
const REFERENCE = { width: 1600, height: 1000 };

export function AuthBackdrop(): JSX.Element {
  const layout = useMemo(() => {
    const scale = REFERENCE.width / REF_WIDTH / 2.6;
    const tileWidth = TILE_WIDTH * scale;
    const tileHeight = TILE_HEIGHT * scale;
    const gap = TILE_GAP * scale;

    const radians = (Math.abs(SLANT_DEGREES) * Math.PI) / 180;
    const plateWidth =
      REFERENCE.width * Math.cos(radians) + REFERENCE.height * Math.sin(radians) + tileWidth * 2;
    const plateHeight =
      REFERENCE.width * Math.sin(radians) + REFERENCE.height * Math.cos(radians) + tileHeight * 2;

    const rowsVisible = Math.ceil(plateHeight / (tileHeight + gap));
    // Every row must be at least as wide as the plate before it repeats,
    // otherwise the hand-off would expose a gap.
    const tilesPerRow = Math.ceil(plateWidth / (tileWidth + gap)) + 1;

    return { tileWidth, tileHeight, gap, plateWidth, plateHeight, rowsVisible, tilesPerRow, scale };
  }, []);

  const rows = useMemo(
    () =>
      Array.from({ length: layout.rowsVisible }, (_, rowIndex) => {
        // Offsetting the pool per row keeps neighbouring tiles from pairing up.
        const photos = Array.from(
          { length: layout.tilesPerRow },
          (_, column) => PHOTOS[(column * 3 + rowIndex * 5) % PHOTOS.length] as string,
        );

        return {
          key: `row-${rowIndex}`,
          photos,
          tintOffset: rowIndex * 2,
          left: rowIndex % 2 === 0,
          seconds: layout.tilesPerRow * SECONDS_PER_TILE * (1 + (rowIndex % 3) * 0.18),
        };
      }),
    [layout.rowsVisible, layout.tilesPerRow],
  );

  const cycle = layout.tilesPerRow * (layout.tileWidth + layout.gap);

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-[52vh] overflow-hidden bg-[#EFEFEF] lg:h-full"
    >
      <div
        className="absolute origin-center"
        style={{
          width: layout.plateWidth,
          height: layout.plateHeight,
          left: '50%',
          top: '50%',
          transform: `translate(-50%, -50%) rotate(${SLANT_DEGREES}deg)`,
          display: 'flex',
          flexDirection: 'column',
          gap: layout.gap,
        }}
      >
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex will-change-transform motion-reduce:!animate-none"
            style={{
              height: layout.tileHeight,
              gap: layout.gap,
              // Each row gets its own named keyframes via a CSS variable for the
              // distance, so one pair of `@keyframes` in `index.css` serves every
              // row at every viewport - see `marquee-left` / `marquee-right`.
              ['--marquee-cycle' as string]: `${cycle}px`,
              animation: `${row.left ? 'marquee-left' : 'marquee-right'} ${row.seconds}s linear infinite`,
            }}
          >
            {/* Rendered twice - see the note on seamlessness at the top. */}
            {row.photos.concat(row.photos).map((uri, index) => {
              const tint = TINTS[(index + row.tintOffset) % TINTS.length];

              return (
                <div
                  key={`${row.key}-${index}`}
                  className="relative shrink-0 overflow-hidden bg-[#E4E4E7]"
                  style={{
                    width: layout.tileWidth,
                    height: layout.tileHeight,
                    borderRadius: TILE_RADIUS * layout.scale,
                  }}
                >
                  <img
                    src={uri}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="size-full object-cover"
                  />
                  {/* The bottom-weighted darkening every tile carries. */}
                  <span className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/[0.62]" />
                  {tint ? (
                    <span className="absolute inset-0" style={{ backgroundColor: tint }} />
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Keeps anything at the very top legible over whatever tile drifts under it. */}
      <div className="absolute inset-x-0 top-0 h-[22%] bg-gradient-to-b from-black/30 to-transparent" />

      {/* The dissolve into the content surface.

          VERTICAL on a phone, matching the app: the wall fades downward into the
          white the form sits on. HORIZONTAL from `lg`, where the form is a card
          on the right rather than a column below - a vertical fade there would
          wash out the middle of the wall and leave the card floating on nothing. */}
      <div
        className="absolute inset-0 lg:hidden"
        style={{
          background:
            'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.10) 44%, rgba(255,255,255,0.55) 70%, rgba(255,255,255,0.94) 90%, #FFFFFF 100%)',
        }}
      />
      <div
        className="absolute inset-0 hidden lg:block"
        style={{
          background:
            'linear-gradient(to right, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.35) 34%, rgba(255,255,255,0.88) 52%, #FFFFFF 62%)',
        }}
      />
    </div>
  );
}
